import * as React from "react";
import { useHistory } from "react-router-dom";
import {
  CellContext,
  ColumnDef
} from "@tanstack/react-table";
import styled from "styled-components";
import { useMappedState } from "redux-react-hook";

import Table from "src/core/dashboard/components/widgets/table/Table";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { ActionButton } from "src/library/components/Button";
import { IAction } from "src/library/components/Button/ActionButton";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { formatPrice } from "src/core/prices/utils/price-helpers";
import {
  Invoice,
  OutstandingBalanceByDays,
  useAccountingApi
} from "src/core/accounting/api/accounting.api";
import { invoiceTableColumns } from "src/core/shared/components/tables/invoiceTableColumns";
import { adminInvoiceTableColumns } from "src/core/shared/components/tables/adminInvoiceTableColumns";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";

type BalanceDaysOption = "total" | "days7" | "days14" | "days30" | "days60" | "days90";

const BALANCE_OPTIONS: Array<{ value: BalanceDaysOption; label: string }> = [
  {
    value: "total",
    label: "All outstanding"
  },
  {
    value: "days7",
    label: "Due in 7 days"
  },
  {
    value: "days14",
    label: "Due in 14 days"
  },
  {
    value: "days30",
    label: "Due in 30 days"
  },
  {
    value: "days60",
    label: "Due in 60 days"
  },
  {
    value: "days90",
    label: "Due in 90 days"
  }
];

const InvoicesTab: React.FC = () => {
  const history = useHistory();
  const [ { drop } ] = useDrop();
  const [ selectedBalanceDays, setSelectedBalanceDays ] = React.useState<BalanceDaysOption>("total");

  // Get current role, terminology, and masquerade mode for role-based columns and filtering
  const { currentRole, terminology } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    terminology: global.settings?.terminology
  }));

  const isAdmin = currentRole?.type === "admin";
  // Use branch-scoped endpoint when drop.id is available (customer users or masquerade mode)
  // This follows the pattern from OrdersPage (LIST_DROP_ORDERS) and ListsPage (GET_DROP_LISTS)
  const [ invoicesResponse, getInvoicesApi ] = useAccountingApi(drop?.customerId ? "LIST_CUSTOMER_INVOICES" : "LIST_INVOICES");

  // Follow established pattern - single parameter object
  const onFetchData = React.useCallback(
    (params: { filters: FilterParams<Invoice>; pagination: PaginatedParams<Invoice> }) => {
      if (drop?.id) {
        getInvoicesApi({
          customerId: drop.customerId,
          filters: { ...params.filters },
          pagination: params.pagination
        });
      } else {
        getInvoicesApi({
          filters: params.filters,
          pagination: params.pagination
        });
      }
    },
    [
      drop?.id,
      drop?.customerId,
      getInvoicesApi
    ],
  );

  // Action button renderer for each row
  const renderActionButton = React.useCallback(
    (info: CellContext<Invoice, unknown>) => {
      const invoice = info.row.original;
      const invoiceUrl = `${isAdmin ? "/admin" : ""}/accounting/invoices/${invoice.id}`;

      const actions: IAction[] = [
        {
          title: "View",
          action: () => {
            history.push(invoiceUrl);
          },
          newTabPath: invoiceUrl
        }
        /*         {
          title: "Pay Now",
          icon: Icons.CREDIT,
          action: () => {
            history.push(`${invoiceUrl}/checkout`);
          },
          newTabPath: `${invoiceUrl}/checkout`
        } */
      ];

      return (
        <ActionButton
          title="Actions"
          items={actions}
        />
      );
    },
    [ history, isAdmin ],
  );

  const tableColumns: Array<ColumnDef<Invoice, keyof Invoice>> = React.useMemo(() => {
    // Use admin columns (with customer/branch) for admin users, customer columns for customer users
    if (isAdmin && terminology) {
      return adminInvoiceTableColumns({
        actionButton: renderActionButton,
        terminology
      }) as Array<ColumnDef<Invoice, keyof Invoice>>;
    }

    return invoiceTableColumns({
      showActions: true,
      actionButton: renderActionButton,
      terminology
    }) as Array<ColumnDef<Invoice, keyof Invoice>>;
  }, [
    renderActionButton,
    isAdmin,
    terminology
  ]);

  // Extract data from backend response structure
  const tableData = React.useMemo(() => {
    return invoicesResponse.data?.invoices?.result || [];
  }, [ invoicesResponse.data?.invoices?.result ]);

  // Get the appropriate balance based on selected days option
  const getSelectedBalance = React.useCallback(
    (balanceByDays: OutstandingBalanceByDays | undefined, fallback: number): number => {
      if (!balanceByDays) {
        return fallback;
      }

      return balanceByDays[ selectedBalanceDays ] ?? fallback;
    },
    [ selectedBalanceDays ],
  );

  const outstandingBalance = getSelectedBalance(invoicesResponse.data?.outstandingBalanceByDays, invoicesResponse.data?.outstandingBalance || 0);

  return (
    <>
      <TabHeader>
        <HeaderLeft>
          <Icon
            icon={Icons.NOTES}
            size={20}
            $fillColor="primaryLight"
          />

          <Text
            fontSize="h3"
            weight="bold"
          >
            Outstanding Invoices
          </Text>
        </HeaderLeft>

        <OutstandingBalanceWrapper>
          <Text
            fontSize="p"
            weight="bold"
          >
            Balance due:
          </Text>

          <BalanceSelect
            value={selectedBalanceDays}
            onChange={e => setSelectedBalanceDays(e.target.value as BalanceDaysOption)}
          >
            {BALANCE_OPTIONS.map(option => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </BalanceSelect>

          <OutstandingBalance
            fontSize="p"
            weight="bold"
          >
            {formatPrice(outstandingBalance)}
          </OutstandingBalance>
        </OutstandingBalanceWrapper>
      </TabHeader>

      <Table
        tableOf="Invoices"
        className="large"
        data={tableData}
        onFetchData={onFetchData}
        defaultPagination={{
          offset: 0,
          limit: 20,
          page: 0,
          sort: [ [ "invoiceDate", "DESC" ] ] as Array<[string, "DESC" | "ASC"]>
        }}
        loading={invoicesResponse.pending}
        pageCount={invoicesResponse.data?.invoices?.pages || 0}
        rowCount={invoicesResponse.data?.invoices?.count || 0}
        error={invoicesResponse.error}
        columns={tableColumns}
      />
    </>
  );
};

export default InvoicesTab;

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.margins.md};
  padding: ${({ theme }) => theme.spacing.padding.sm};
  background: ${({ theme }) => theme.colors.lightestGrey};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.sm};
`;

const OutstandingBalanceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.sm};
`;

const OutstandingBalance = styled(Text)`
  color: ${({ theme }) => theme.colors.error};
`;

const BalanceSelect = styled.select`
  appearance: none;
  padding: ${({ theme }) => theme.spacing.padding.xs} ${({ theme }) => theme.spacing.padding.md};
  padding-right: ${({ theme }) => theme.spacing.padding.lg};
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  font-weight: ${({ theme }) => theme.fonts.weights.bold};
  color: ${({ theme }) => theme.colors.error};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: ${({ theme }) => theme.borderRadius};
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primaryLight};
  }
`;
